import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://api.littleexplorers.help";

const UploadContext = createContext(null);

// Helper function to handle API responses with 403 check
const handleApiResponse = async (response, onUnauthorized) => {
  if (response.status === 403) {
    const errorData = await response.json().catch(() => ({}));
    if (errorData.code === "FORBIDDEN_DOMAIN") {
      onUnauthorized?.();
      throw new Error("Unauthorized email domain");
    }
  }
  return response;
};

export const UploadProvider = ({ children, getAccessToken, onUnauthorized }) => {
  const [activeUploads, setActiveUploads] = useState([]);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch upload history
  const fetchHistory = useCallback(
    async (options = {}) => {
      try {
        setIsLoading(true);
        const token = getAccessToken();
        if (!token) throw new Error("Not authenticated");

        const params = new URLSearchParams();
        if (options.type) params.append("type", options.type);
        if (options.status) params.append("status", options.status);
        if (options.limit) params.append("limit", options.limit.toString());
        if (options.offset) params.append("offset", options.offset.toString());

        const response = await fetch(`${API_BASE_URL}/uploads?${params}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        await handleApiResponse(response, onUnauthorized);

        if (!response.ok) {
          throw new Error("Failed to fetch upload history");
        }

        const data = await response.json();
        setUploadHistory(data.jobs || []);
        return data;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [getAccessToken, onUnauthorized],
  );

  // Start a new upload
  const startUpload = useCallback(
    async (file, type, organizationId = null) => {
      try {
        const token = getAccessToken();
        if (!token) throw new Error("Not authenticated");

        const formData = new FormData();
        formData.append("csvFile", file);
        formData.append("type", type);
        if (organizationId) {
          formData.append("organizationId", organizationId);
        }

        const response = await fetch(`${API_BASE_URL}/uploads/csv`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });

        await handleApiResponse(response, onUnauthorized);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Upload failed");
        }

        const data = await response.json();

        // Add to active uploads
        const newUpload = {
          jobId: data.jobId,
          filename: file.name,
          type,
          status: data.status,
          totalRows: data.totalRows,
          processedRows: 0,
          successfulRows: 0,
          failedRows: 0,
          progress: 0,
        };

        setActiveUploads((prev) => [...prev, newUpload]);

        // Start polling for this upload
        pollJobStatus(data.jobId, token);

        return data;
      } catch (err) {
        setError(err.message);
        throw err;
      }
    },
    [getAccessToken, onUnauthorized],
  );

  // Poll job status
  const pollJobStatus = useCallback(
    async (jobId, token) => {
      const pollInterval = setInterval(async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/uploads/${jobId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            clearInterval(pollInterval);
            return;
          }

          const job = await response.json();

          // Update active upload
          setActiveUploads((prev) =>
            prev.map((upload) =>
              upload.jobId === jobId
                ? {
                    ...upload,
                    status: job.status,
                    processedRows: job.processedRows,
                    successfulRows: job.successfulRows,
                    failedRows: job.failedRows,
                    progress:
                      job.totalRows > 0
                        ? Math.round((job.processedRows / job.totalRows) * 100)
                        : 0,
                    errors: job.processingErrors || [],
                  }
                : upload,
            ),
          );

          // Stop polling if job is complete or failed
          if (["completed", "failed", "cancelled"].includes(job.status)) {
            clearInterval(pollInterval);
            // Move from active to history
            setTimeout(() => {
              setActiveUploads((prev) => prev.filter((u) => u.jobId !== jobId));
              fetchHistory();
            }, 3000);
          }
        } catch (err) {
          console.error("Error polling job status:", err);
          clearInterval(pollInterval);
        }
      }, 2000); // Poll every 2 seconds

      // Store interval ID for cleanup
      return () => clearInterval(pollInterval);
    },
    [fetchHistory],
  );

  // Cancel an upload
  const cancelUpload = useCallback(
    async (jobId) => {
      try {
        const token = getAccessToken();
        if (!token) throw new Error("Not authenticated");

        const response = await fetch(
          `${API_BASE_URL}/uploads/${jobId}/cancel`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        await handleApiResponse(response, onUnauthorized);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Cancel failed");
        }

        // Update local state
        setActiveUploads((prev) =>
          prev.map((upload) =>
            upload.jobId === jobId
              ? { ...upload, status: "cancelled" }
              : upload,
          ),
        );

        return true;
      } catch (err) {
        setError(err.message);
        throw err;
      }
    },
    [getAccessToken, onUnauthorized],
  );

  // Get upload details
  const getUploadDetails = useCallback(
    async (jobId) => {
      try {
        const token = getAccessToken();
        if (!token) throw new Error("Not authenticated");

        const response = await fetch(`${API_BASE_URL}/uploads/${jobId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        await handleApiResponse(response, onUnauthorized);

        if (!response.ok) {
          throw new Error("Failed to fetch upload details");
        }

        return await response.json();
      } catch (err) {
        setError(err.message);
        throw err;
      }
    },
    [getAccessToken, onUnauthorized],
  );

  // Download errors CSV
  const downloadErrorsCsv = useCallback((errors, filename) => {
    if (!errors || errors.length === 0) return;

    const headers = ["Row", "Error", "Data"];
    const rows = errors.map((err) => [
      err.row,
      err.error,
      JSON.stringify(err.data || {}),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}_errors.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, []);

  const value = {
    activeUploads,
    uploadHistory,
    isLoading,
    error,
    startUpload,
    cancelUpload,
    fetchHistory,
    getUploadDetails,
    downloadErrorsCsv,
  };

  return (
    <UploadContext.Provider value={value}>{children}</UploadContext.Provider>
  );
};

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error("useUpload must be used within an UploadProvider");
  }
  return context;
};

export default UploadContext;
